import { type FormEvent, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Cloud, GitBranch, TrendingUp } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Alert } from '../../../components/ui/alert';
import { createDomain, listDomains, updateDomain } from '../../../lib/api';
import { StepScaffold } from '../StepScaffold';

/** What a domain ties together — shown as a small reference so the term lands
 * without a wall of prose. */
const CONTAINS = [
  { icon: Cloud, text: 'Cloud accounts & projects — AWS, GCP, Azure' },
  { icon: GitBranch, text: 'The code repositories that deploy into them' },
  { icon: TrendingUp, text: 'Its spend, waste, and realized savings over time' },
];

/** Step 2 — name the first Engineering Domain. Idempotent across back/forward: if a
 * domain was already created (the user stepped back here), prefill its name and
 * RENAME it on submit rather than creating a duplicate. */
export function DomainStep({
  onDone,
  eyebrow = 'Name your first domain',
}: {
  onDone: () => void;
  eyebrow?: string;
}) {
  const qc = useQueryClient();
  const domains = useQuery({ queryKey: ['domains'], queryFn: listDomains });
  const existing = domains.data?.[0];

  const [name, setName] = useState('');
  // Prefill the field with the existing domain's name once it loads (e.g. the user
  // stepped back to this screen). Only seeds an untouched field.
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (existing && !touched && name === '') setName(existing.name);
  }, [existing, touched, name]);

  const mutation = useMutation({
    mutationFn: () =>
      existing ? updateDomain(existing.id, { name: name.trim() }) : createDomain(name.trim()),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['domains'] });
      onDone();
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (name.trim()) mutation.mutate();
  }

  return (
    <form onSubmit={submit}>
      <StepScaffold
        eyebrow={eyebrow}
        title="Create your first domain"
        lead="A domain is the home for a slice of your infrastructure — its clouds, code, cost and savings in one place. Most teams start with one and add more as they grow."
        safety="Nothing is connected yet — this just creates a space to organize your first analysis."
        footer={
          <Button type="submit" disabled={!name.trim() || mutation.isPending}>
            {mutation.isPending ? 'Saving…' : existing ? 'Save & continue' : 'Create domain'}
          </Button>
        }
      >
        <div className="space-y-1.5">
          <label htmlFor="domain-name" className="block text-sm font-semibold text-bone">
            Domain name
          </label>
          <Input
            id="domain-name"
            autoFocus
            required
            placeholder="e.g. Payroll Infrastructure"
            value={name}
            onChange={(e) => {
              setTouched(true);
              setName(e.target.value);
            }}
          />
          <p className="text-xs text-muted">
            Use the name your team already says out loud — a product, a service, or one team’s
            architecture.
          </p>
        </div>

        <div className="rounded-md border border-hairline bg-surface-2 p-4">
          <p className="font-mono text-[0.7rem] font-semibold uppercase tracking-wide text-muted">
            A domain groups
          </p>
          <ul className="mt-3 space-y-2.5">
            {CONTAINS.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-2.5 text-sm text-haze">
                <Icon className="h-4 w-4 flex-none text-iris" aria-hidden />
                {text}
              </li>
            ))}
          </ul>
        </div>

        <p className="text-xs text-muted">
          You can rename it or add more domains later (up to 10).
        </p>

        {mutation.isError && (
          <Alert variant="danger">
            {mutation.error instanceof Error ? mutation.error.message : 'Could not save domain'}
          </Alert>
        )}
      </StepScaffold>
    </form>
  );
}
